// Zod schemas shared between web and api.
// As CRUD endpoints are introduced (Steps 4–11), define their request/response
// schemas here so both sides reference the same shape.

import { z } from 'zod';
import type { VisibilityRule as VisibilityRuleType } from './progress.ts';

export const QuestionType = z.enum([
  'yesno',
  'text',
  'select',
  'multiselect',
  'document_adopt',
  'document_upload',
  'multiselect_uploads',
  'signature',
  'informational',
  'image_info',
]);
export type QuestionType = z.infer<typeof QuestionType>;

export const CompanyStatus = z.enum([
  'invited',
  'in_progress',
  'review',
  'changes',
  'compliant',
  'expired',
  'deactivated',
]);
export type CompanyStatus = z.infer<typeof CompanyStatus>;

export const SubmissionStatus = z.enum(['pending', 'approved', 'rejected', 'not_applicable']);
export type SubmissionStatus = z.infer<typeof SubmissionStatus>;

export const AdoptionMode = z.enum(['adopted', 'uploaded']);
export type AdoptionMode = z.infer<typeof AdoptionMode>;

export const ActorType = z.enum(['admin', 'company', 'system']);
export type ActorType = z.infer<typeof ActorType>;

// ----------------------------------------------------------------------
// Questions / adoption triggers
// ----------------------------------------------------------------------

const ConditionValue = z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]);

export const QuestionCondition = z.object({
  value: ConditionValue,
});
export type QuestionCondition = z.infer<typeof QuestionCondition>;

// Flat AND/OR visibility expression: one combinator over 1-20 leaf
// conditions. (Storage and the shared evaluator tolerate nested trees for
// legacy/out-of-band data, but the write path only accepts flat rules —
// see the spec's Revision 2026-06-06.)
const VisibilityRuleLeaf = z.object({ q: z.string().uuid(), value: ConditionValue });

export const VisibilityRule: z.ZodType<VisibilityRuleType> = z.object({
  op: z.enum(['and', 'or']),
  children: z.array(VisibilityRuleLeaf).min(1).max(20),
});
// Note: no `export type VisibilityRule` alias here — the canonical TS type
// is `VisibilityRule` from progress.ts. A duplicate alias would conflict in
// the barrel (index.ts uses `export *` over both files).

export const AdoptionTriggerInput = z.object({
  trigger_value: z.string().min(1),
  document_id: z.string().uuid(),
  adopt_only: z.boolean().default(false),
});
export type AdoptionTriggerInput = z.infer<typeof AdoptionTriggerInput>;

const baseQuestionFields = {
  type: QuestionType,
  // A question belongs to a specific questionnaire AND category (migration 0032):
  // the category is a shared grouping, the questionnaire scopes the question set.
  questionnaire_id: z.string().uuid(),
  category_id: z.string().uuid(),
  label: z.string().min(1),
  help_text: z.string().nullable().optional(),
  required: z.boolean().optional(),
  requires_upload: z.boolean().optional(),
  include_in_compliance_documents: z.boolean().optional(),
  upload_label: z.string().nullable().optional(),
  options: z.array(z.string()).nullable().optional(),
  parent_id: z.string().uuid().nullable().optional(),
  condition: QuestionCondition.nullable().optional(),
  visibility_rule: VisibilityRule.nullable().optional(),
  adoptable_doc_id: z.string().uuid().nullable().optional(),
  // Links a document_upload question to a document type so uploads against it
  // are run through AI verification of the type's configured fields.
  document_type_id: z.string().uuid().nullable().optional(),
  video_url: z.string().url().nullable().optional(),
  image_url: z.string().url().nullable().optional(),
  display_order: z.number().int().min(0).optional(),
  adoption_triggers: z.array(AdoptionTriggerInput).optional(),
};

export const QuestionCreateInput = z.object(baseQuestionFields).superRefine(crossFieldChecks);
export type QuestionCreateInput = z.infer<typeof QuestionCreateInput>;

export const QuestionUpdateInput = z
  .object({
    type: QuestionType.optional(),
    questionnaire_id: z.string().uuid().optional(),
    category_id: z.string().uuid().optional(),
    label: z.string().min(1).optional(),
    help_text: z.string().nullable().optional(),
    required: z.boolean().optional(),
    requires_upload: z.boolean().optional(),
    include_in_compliance_documents: z.boolean().optional(),
    upload_label: z.string().nullable().optional(),
    options: z.array(z.string()).nullable().optional(),
    parent_id: z.string().uuid().nullable().optional(),
    condition: QuestionCondition.nullable().optional(),
    visibility_rule: VisibilityRule.nullable().optional(),
    adoptable_doc_id: z.string().uuid().nullable().optional(),
    document_type_id: z.string().uuid().nullable().optional(),
    video_url: z.string().url().nullable().optional(),
    image_url: z.string().url().nullable().optional(),
    display_order: z.number().int().min(0).optional(),
    adoption_triggers: z.array(AdoptionTriggerInput).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.type !== undefined) {
      crossFieldChecks(value as never, ctx);
      return;
    }
    // Partial updates skip the full cross-field checks (they can't see unset
    // fields), but combining both visibility mechanisms in ONE payload is
    // always wrong regardless of what's stored.
    visibilityExclusivityChecks(value, ctx);
  });
export type QuestionUpdateInput = z.infer<typeof QuestionUpdateInput>;

export const QuestionsReorderInput = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type QuestionsReorderInput = z.infer<typeof QuestionsReorderInput>;

// Body of PUT /api/questions/:id/option-links — reconciles which options of a
// multiselect parent show question :id as a per-option follow-up. An empty
// options array unlinks the question entirely.
export const QuestionOptionLinksInput = z.object({
  parent_id: z.string().uuid(),
  options: z.array(z.string().min(1).max(255)).max(100),
});
export type QuestionOptionLinksInput = z.infer<typeof QuestionOptionLinksInput>;

function crossFieldChecks(
  value: {
    type?: QuestionType | undefined;
    parent_id?: string | null | undefined;
    condition?: QuestionCondition | null | undefined;
    visibility_rule?: VisibilityRuleType | null | undefined;
    adoptable_doc_id?: string | null | undefined;
    document_type_id?: string | null | undefined;
    options?: string[] | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  visibilityExclusivityChecks(value, ctx);
  if (
    value.document_type_id &&
    value.type !== 'document_upload' &&
    value.type !== 'multiselect_uploads'
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['document_type_id'],
      message: 'document_type_id only valid for document_upload or multiselect_uploads',
    });
  }
  if (value.parent_id && !value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['condition'],
      message: 'condition is required when parent_id is set',
    });
  }
  if (!value.parent_id && value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['condition'],
      message: 'condition only allowed when parent_id is set',
    });
  }
  if (
    value.adoptable_doc_id &&
    value.type !== 'document_adopt' &&
    value.type !== 'document_upload'
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adoptable_doc_id'],
      message: 'adoptable_doc_id only valid for document_adopt or document_upload',
    });
  }
  if ((value as { video_url?: string | null }).video_url && value.type !== 'informational') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['video_url'],
      message: 'video_url is only valid on informational questions',
    });
  }
  if ((value as { image_url?: string | null }).image_url && value.type !== 'image_info') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['image_url'],
      message: 'image_url is only valid on image_info questions',
    });
  }
  if (
    (value.type === 'select' ||
      value.type === 'multiselect' ||
      value.type === 'multiselect_uploads') &&
    (!value.options || value.options.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'options is required for select / multiselect questions',
    });
  }
}

function visibilityExclusivityChecks(
  value: {
    parent_id?: string | null | undefined;
    condition?: QuestionCondition | null | undefined;
    visibility_rule?: VisibilityRuleType | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.visibility_rule && value.parent_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['visibility_rule'],
      message: 'visibility_rule cannot be combined with parent_id',
    });
  }
  if (value.visibility_rule && value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['visibility_rule'],
      message: 'visibility_rule cannot be combined with condition',
    });
  }
}

// ----------------------------------------------------------------------
// Questionnaires
// ----------------------------------------------------------------------

export const QuestionnaireAudience = z.enum([
  'company',
  'driver',
  'vehicle',
  'contractor',
  'visitor',
  'admin_driver',
  'admin_asset',
  // The "Audit" questionnaire type: assigned to an asset for an auditor to
  // perform (see AssetAuditAssignInput). Distinct from vehicle/admin_asset.
  'audit',
]);
export type QuestionnaireAudience = z.infer<typeof QuestionnaireAudience>;

export const QuestionnaireCreateInput = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  audience: QuestionnaireAudience,
  contractor_types: z.array(z.string().min(1).max(120)).nullable().optional(),
  // Which global question categories this questionnaire includes (many-to-many,
  // see migration 0031). Omitted = no change / none.
  category_ids: z.array(z.string().uuid()).optional(),
  display_order: z.number().int().min(0).optional(),
  is_active: z.boolean().optional(),
});
export type QuestionnaireCreateInput = z.infer<typeof QuestionnaireCreateInput>;

export const QuestionnaireUpdateInput = z
  .object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    audience: QuestionnaireAudience.optional(),
    contractor_types: z.array(z.string().min(1).max(120)).nullable().optional(),
    category_ids: z.array(z.string().uuid()).optional(),
    display_order: z.number().int().min(0).optional(),
    is_active: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type QuestionnaireUpdateInput = z.infer<typeof QuestionnaireUpdateInput>;

// ----------------------------------------------------------------------
// Question categories
// ----------------------------------------------------------------------

// Categories are now global, standalone entities (see migration 0031). They are
// no longer created against a single questionnaire; a questionnaire ticks which
// categories it includes via `category_ids` on the questionnaire create/update.
export const QuestionCategoryCreateInput = z.object({
  name: z.string().min(1).max(255),
  display_order: z.number().int().min(0).optional(),
});
export type QuestionCategoryCreateInput = z.infer<typeof QuestionCategoryCreateInput>;

export const QuestionCategoryUpdateInput = z
  .object({
    name: z.string().min(1).max(255).optional(),
    display_order: z.number().int().min(0).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type QuestionCategoryUpdateInput = z.infer<typeof QuestionCategoryUpdateInput>;

export const QuestionCategoriesReorderInput = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type QuestionCategoriesReorderInput = z.infer<typeof QuestionCategoriesReorderInput>;

// ----------------------------------------------------------------------
// Team regions (first-class entity since 0053; managed in Settings)
// ----------------------------------------------------------------------

export const TeamRegionCreateInput = z.object({
  name: z.string().min(1).max(120),
});
export type TeamRegionCreateInput = z.infer<typeof TeamRegionCreateInput>;

export const TeamRegionUpdateInput = z
  .object({
    name: z.string().min(1).max(120).optional(),
    display_order: z.number().int().min(0).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type TeamRegionUpdateInput = z.infer<typeof TeamRegionUpdateInput>;

export const TeamRegionsReorderInput = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type TeamRegionsReorderInput = z.infer<typeof TeamRegionsReorderInput>;

// ----------------------------------------------------------------------
// Companies
// ----------------------------------------------------------------------

const companyInfoFields = {
  contractor_type: z.string().max(255).nullable().optional(),
  company_number: z.string().max(255).nullable().optional(),
  contract_number: z.string().max(255).nullable().optional(),
  team_region: z.string().max(255).nullable().optional(),
  abn: z.string().max(255).nullable().optional(),
  legal_entity_name: z.string().max(255).nullable().optional(),
  address_state: z.string().max(50).nullable().optional(),
  address_postcode: z.string().max(20).nullable().optional(),
  suburb: z.string().max(255).nullable().optional(),
  trustee_info: z.string().max(1000).nullable().optional(),
  primary_contact_name: z.string().max(255).nullable().optional(),
  contact_role: z.string().max(255).nullable().optional(),
  contact_phone: z.string().max(50).nullable().optional(),
  contact_email: z.string().email().max(255).nullable().optional(),
  employee_count: z.number().int().min(0).max(10_000_000).nullable().optional(),
};

export const CompanyCreateInput = z.object({
  company_name: z.string().min(1).max(255),
  contact_name: z.string().min(1).max(255),
  email: z.string().email().max(255),
  ...companyInfoFields,
});
export type CompanyCreateInput = z.infer<typeof CompanyCreateInput>;

export const CompanyUpdateInput = z
  .object({
    company_name: z.string().min(1).max(255).optional(),
    contact_name: z.string().min(1).max(255).optional(),
    email: z.string().email().max(255).optional(),
    // Deactivation is managed exclusively by the deactivate/reactivate
    // endpoints (they own the snapshot column) — not settable here.
    status: CompanyStatus.exclude(['deactivated']).optional(),
    expires_at: z.string().datetime().nullable().optional(),
    ...companyInfoFields,
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type CompanyUpdateInput = z.infer<typeof CompanyUpdateInput>;

// Subset the company itself can edit through the portal — must not allow
// changing pre-filled fields (contractor_type, company_number, contract_number,
// team_region) or status / contact_name / email used for invites.
export const CompanyInfoSelfUpdateInput = z
  .object({
    abn: z.string().max(255).nullable().optional(),
    legal_entity_name: z.string().max(255).nullable().optional(),
    address_state: z.string().max(50).nullable().optional(),
    address_postcode: z.string().max(20).nullable().optional(),
    suburb: z.string().max(255).nullable().optional(),
    trustee_info: z.string().max(1000).nullable().optional(),
    primary_contact_name: z.string().max(255).nullable().optional(),
    contact_role: z.string().max(255).nullable().optional(),
    contact_phone: z.string().max(50).nullable().optional(),
    contact_email: z.string().email().max(255).nullable().optional(),
    employee_count: z.number().int().min(0).max(10_000_000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type CompanyInfoSelfUpdateInput = z.infer<typeof CompanyInfoSelfUpdateInput>;

export const CompanyInviteInput = z.object({
  ttl_days: z.number().int().min(1).max(60).optional(),
});
export type CompanyInviteInput = z.infer<typeof CompanyInviteInput>;

export const AbnLookupInput = z.object({ abn: z.string().min(1).max(20) });
export type AbnLookupInput = z.infer<typeof AbnLookupInput>;

export type AbnLookupResult =
  | {
      ok: true;
      verified: boolean;
      abn: string;
      legalEntityName: string | null;
      addressState: string | null;
      addressPostcode: string | null;
      abnStatus: string | null;
    }
  | { ok: false; reason: 'invalid_abn' | 'not_configured' | 'lookup_failed'; detail?: string };

// ----------------------------------------------------------------------
// Drivers (children of a company)
// ----------------------------------------------------------------------

export const DriverCreateInput = z.object({
  name: z.string().min(1).max(255),
  email: z.string().email().max(255),
  phone: z.string().max(50).nullable().optional(),
  contractor_type: z.string().min(1).max(120).nullable().optional(),
  team_region: z.string().max(255).nullable().optional(),
});
export type DriverCreateInput = z.infer<typeof DriverCreateInput>;

// Fields a driver's owner may edit regardless of role. Status is intentionally
// NOT here — a driver's compliance status is set only by admin review/roll-up.
const driverEditableFields = {
  name: z.string().min(1).max(255).optional(),
  email: z.string().email().max(255).optional(),
  phone: z.string().max(50).nullable().optional(),
};

// Admin driver update: may also set status.
export const DriverUpdateInput = z
  .object({
    ...driverEditableFields,
    // 'deactivated' exists on the shared enum for companies only.
    status: CompanyStatus.exclude(['deactivated']).optional(),
    team_region: z.string().max(255).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type DriverUpdateInput = z.infer<typeof DriverUpdateInput>;

// Company-facing driver update (PATCH /api/me/drivers/:id): name/email/phone
// only. Omits status so a company can never set a driver's compliance — that is
// admin-only. Mirrors the CompanyInfoSelfUpdateInput self-update pattern.
export const DriverSelfUpdateInput = z
  .object({ ...driverEditableFields })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type DriverSelfUpdateInput = z.infer<typeof DriverSelfUpdateInput>;

export const DriverInviteInput = z.object({
  ttl_days: z.number().int().min(1).max(60).optional(),
});
export type DriverInviteInput = z.infer<typeof DriverInviteInput>;

// ----------------------------------------------------------------------
// Review actions (admin)
// ----------------------------------------------------------------------

export const ReviewDecision = z.enum(['approve', 'reject', 'reset', 'na', 'comment']);
export type ReviewDecision = z.infer<typeof ReviewDecision>;

export const ReviewReferenceUpload = z.object({
  path: z.string().min(1).max(1024),
  name: z.string().min(1).max(255),
});
export type ReviewReferenceUpload = z.infer<typeof ReviewReferenceUpload>;

export const ReviewInput = z
  .object({
    decision: ReviewDecision,
    comment: z.string().min(1).max(2000).optional(),
    // Optional reference document for a change request: either adopt an existing
    // library document by id, or attach a one-off uploaded file. Mutually
    // exclusive; only meaningful for 'reject'.
    reference_document_id: z.string().uuid().optional(),
    reference_upload: ReviewReferenceUpload.optional(),
  })
  .superRefine((value, ctx) => {
    const hasComment = !!value.comment && value.comment.trim().length > 0;
    const hasReference = !!value.reference_document_id || !!value.reference_upload;
    // Not Compliant requires SOMETHING actionable for the company: a comment
    // and/or a reference document.
    if (value.decision === 'reject' && !hasComment && !hasReference) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'provide a comment or a reference document when marking not compliant',
      });
    }
    // A standalone comment must actually carry text.
    if (value.decision === 'comment' && !hasComment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'comment is required',
      });
    }
    if (value.reference_document_id && value.reference_upload) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reference_upload'],
        message: 'provide either reference_document_id or reference_upload, not both',
      });
    }
  });
export type ReviewInput = z.infer<typeof ReviewInput>;

export const BulkApproveInput = z
  .object({
    expires_in_days: z.number().int().min(1).max(3650).optional(),
  })
  .optional();
export type BulkApproveInput = z.infer<typeof BulkApproveInput>;

// ----------------------------------------------------------------------
// Submissions
// ----------------------------------------------------------------------

export const SignatureAnswer = z.object({
  signature_data_url: z.string().min(1),
  signer_name: z.string().min(1),
  signed_at: z.string().datetime(),
});
export type SignatureAnswer = z.infer<typeof SignatureAnswer>;

export const InformationalAnswer = z.object({
  watched_at: z.string().datetime(),
});
export type InformationalAnswer = z.infer<typeof InformationalAnswer>;

export const SubmissionAnswerInput = z.object({
  answer: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      SignatureAnswer,
      InformationalAnswer,
      z.null(),
    ])
    .optional(),
  comment: z.string().nullable().optional(),
});
export type SubmissionAnswerInput = z.infer<typeof SubmissionAnswerInput>;

export const SubmissionFileUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
  option_key: z.string().min(1).max(255).nullable().optional(),
});
export type SubmissionFileUploadUrlInput = z.infer<typeof SubmissionFileUploadUrlInput>;

export const SubmissionFileRegisterInput = z.object({
  file_path: z.string().min(1),
  file_name: z.string().min(1).max(255),
  size_bytes: z.number().int().min(0),
  mime_type: z.string().min(1).max(255),
  option_key: z.string().min(1).max(255).nullable().optional(),
  expires_at: z.string().datetime().nullable().optional(),
});
export type SubmissionFileRegisterInput = z.infer<typeof SubmissionFileRegisterInput>;

export const SubmissionFileUpdateInput = z
  .object({
    expires_at: z.string().datetime().nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type SubmissionFileUpdateInput = z.infer<typeof SubmissionFileUpdateInput>;

export const SubmissionAdoptionInput = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('adopted'),
    option_key: z.string().min(1).max(255).nullable().optional(),
  }),
  z.object({
    mode: z.literal('uploaded'),
    file_path: z.string().min(1),
    option_key: z.string().min(1).max(255).nullable().optional(),
  }),
]);
export type SubmissionAdoptionInput = z.infer<typeof SubmissionAdoptionInput>;

// ----------------------------------------------------------------------
// Documents
// ----------------------------------------------------------------------

export const DocumentUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
});
export type DocumentUploadUrlInput = z.infer<typeof DocumentUploadUrlInput>;

export const DocumentCreateInput = z.object({
  name: z.string().min(1),
  category: z.string().min(1),
  version: z.string().min(1),
  file_path: z.string().min(1),
  is_adoptable: z.boolean().default(true),
});
export type DocumentCreateInput = z.infer<typeof DocumentCreateInput>;

export const DocumentUpdateInput = z
  .object({
    name: z.string().min(1).optional(),
    category: z.string().min(1).optional(),
    version: z.string().min(1).optional(),
    is_adoptable: z.boolean().optional(),
    current_version: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type DocumentUpdateInput = z.infer<typeof DocumentUpdateInput>;

// ----------------------------------------------------------------------
// Document types + AI verification config
// ----------------------------------------------------------------------

export const DocumentTypeFieldInput = z.object({
  field_key: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9_]+$/, 'use lowercase letters, numbers and underscores'),
  label: z.string().min(1).max(255),
  description: z.string().max(1000).nullable().optional(),
});
export type DocumentTypeFieldInput = z.infer<typeof DocumentTypeFieldInput>;

export const DocumentTypeCreateInput = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  auto_pass_threshold: z.number().min(0).max(1).optional(),
  is_active: z.boolean().optional(),
  // The full set of fields to verify. Replaces the type's fields when provided.
  fields: z.array(DocumentTypeFieldInput).max(50).optional(),
});
export type DocumentTypeCreateInput = z.infer<typeof DocumentTypeCreateInput>;

export const DocumentTypeUpdateInput = z
  .object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    auto_pass_threshold: z.number().min(0).max(1).optional(),
    is_active: z.boolean().optional(),
    fields: z.array(DocumentTypeFieldInput).max(50).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type DocumentTypeUpdateInput = z.infer<typeof DocumentTypeUpdateInput>;

// Human resolution of an escalated (needs_review) verification.
export const VerificationReviewInput = z.object({
  decision: z.enum(['pass', 'fail']),
  note: z.string().max(2000).nullable().optional(),
});
export type VerificationReviewInput = z.infer<typeof VerificationReviewInput>;

// ----------------------------------------------------------------------
// Training modules (hosted HTML)
// ----------------------------------------------------------------------

// Who a training module is assigned to. 'both' (the default) assigns it to
// every company and driver; 'company' / 'driver' restrict it to that portal's
// Training tab.
export const TrainingModuleAudience = z.enum(['company', 'driver', 'both']);
export type TrainingModuleAudience = z.infer<typeof TrainingModuleAudience>;

export const TrainingModuleUpdateInput = z
  .object({
    title: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    audience: TrainingModuleAudience.optional(),
    is_active: z.boolean().optional(),
    pass_threshold: z.number().int().min(0).max(100).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type TrainingModuleUpdateInput = z.infer<typeof TrainingModuleUpdateInput>;

export const TrainingUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
});
export type TrainingUploadUrlInput = z.infer<typeof TrainingUploadUrlInput>;

// Registers an uploaded standalone HTML file as a hosted (source=html) module.
export const TrainingHtmlRegisterInput = z.object({
  title: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  html_path: z.string().min(1),
  html_file_name: z.string().min(1).max(255),
  pass_threshold: z.number().int().min(0).max(100).default(80),
  audience: TrainingModuleAudience.optional(),
});
export type TrainingHtmlRegisterInput = z.infer<typeof TrainingHtmlRegisterInput>;

// Body of the public POST /api/training-attempts/:token/complete. Raw counts
// only — the server computes score_pct and the pass/fail verdict.
export const TrainingAttemptCompleteInput = z
  .object({
    correct: z.number().int().min(0),
    total: z.number().int().min(1).max(500),
  })
  .refine((v) => v.correct <= v.total, {
    message: 'correct cannot exceed total',
    path: ['correct'],
  });
export type TrainingAttemptCompleteInput = z.infer<typeof TrainingAttemptCompleteInput>;

// ----------------------------------------------------------------------
// Assets (+ insurance, registration, photos)
// ----------------------------------------------------------------------

const assetFields = {
  make: z.string().max(120).nullable().optional(),
  model: z.string().max(120).nullable().optional(),
  year: z.number().int().min(1900).max(2100).nullable().optional(),
  registration_plate: z.string().max(60).nullable().optional(),
  asset_type: z.string().max(120).nullable().optional(),
  vin: z.string().max(120).nullable().optional(),
  colour: z.string().max(60).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
};

export const AssetCreateInput = z.object({
  ...assetFields,
  status: z.enum(['active', 'inactive']).optional(),
  team_region: z.string().max(255).nullable().optional(),
});
export type AssetCreateInput = z.infer<typeof AssetCreateInput>;

export const AssetUpdateInput = z
  .object({
    ...assetFields,
    status: z.enum(['active', 'inactive']).optional(),
    team_region: z.string().max(255).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type AssetUpdateInput = z.infer<typeof AssetUpdateInput>;

export const AssetInsuranceInput = z.object({
  insurer: z.string().min(1).max(255),
  policy_number: z.string().max(120).nullable().optional(),
  cover_type: z.string().max(120).nullable().optional(),
  start_date: z.string().date().nullable().optional(),
  renewal_date: z.string().date().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type AssetInsuranceInput = z.infer<typeof AssetInsuranceInput>;

export const AssetRegistrationInput = z.object({
  registration_number: z.string().max(120).nullable().optional(),
  renewal_date: z.string().date().nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type AssetRegistrationInput = z.infer<typeof AssetRegistrationInput>;

export const AssetPhotoUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
});
export type AssetPhotoUploadUrlInput = z.infer<typeof AssetPhotoUploadUrlInput>;

export const AssetPhotoRegisterInput = z.object({
  file_path: z.string().min(1),
  file_name: z.string().min(1).max(255),
  caption: z.string().max(500).nullable().optional(),
});
export type AssetPhotoRegisterInput = z.infer<typeof AssetPhotoRegisterInput>;

// ----------------------------------------------------------------------
// Asset audits (operating-company run) + schedules + corrective actions
// ----------------------------------------------------------------------

const RecurrenceEnum = z.enum(['none', 'weekly', 'monthly', 'quarterly', 'biannual', 'annual']);

export const AssetAuditScheduleCreateInput = z.object({
  questionnaire_id: z.string().uuid(),
  recurrence: RecurrenceEnum.default('quarterly'),
  next_due_date: z.string().date().nullable().optional(),
});
export type AssetAuditScheduleCreateInput = z.infer<typeof AssetAuditScheduleCreateInput>;

export const AssetAuditScheduleUpdateInput = z
  .object({
    recurrence: RecurrenceEnum.optional(),
    next_due_date: z.string().date().nullable().optional(),
    is_active: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type AssetAuditScheduleUpdateInput = z.infer<typeof AssetAuditScheduleUpdateInput>;

export const AssetAuditCreateInput = z.object({
  questionnaire_id: z.string().uuid(),
  schedule_id: z.string().uuid().nullable().optional(),
  // When set, the audit is created in the 'assigned' state for this auditor to
  // perform (rather than started in-place by the acting admin). Required when the
  // chosen questionnaire is of the 'audit' audience — an audit questionnaire is
  // never assigned to an asset without an auditor.
  auditor_user_id: z.string().uuid().nullable().optional(),
});
export type AssetAuditCreateInput = z.infer<typeof AssetAuditCreateInput>;

export const AssetAuditAnswerInput = z.object({
  answer: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()])
    .optional(),
  comment: z.string().max(2000).nullable().optional(),
  is_compliant: z.boolean().nullable().optional(),
});
export type AssetAuditAnswerInput = z.infer<typeof AssetAuditAnswerInput>;

export const AssetAuditAnswerPhotoInput = z.object({
  photo_path: z.string().min(1),
});
export type AssetAuditAnswerPhotoInput = z.infer<typeof AssetAuditAnswerPhotoInput>;

export const AuditPhotoUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
});
export type AuditPhotoUploadUrlInput = z.infer<typeof AuditPhotoUploadUrlInput>;

export const AssetAuditCompleteInput = z.object({
  status: z.enum(['compliant', 'not_compliant']),
  notes: z.string().max(2000).nullable().optional(),
});
export type AssetAuditCompleteInput = z.infer<typeof AssetAuditCompleteInput>;

export const CorrectiveActionRespondInput = z.object({
  comment: z.string().min(1).max(2000),
  photo_path: z.string().min(1).nullable().optional(),
});
export type CorrectiveActionRespondInput = z.infer<typeof CorrectiveActionRespondInput>;

export const CorrectiveActionReviewInput = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().max(2000).nullable().optional(),
});
export type CorrectiveActionReviewInput = z.infer<typeof CorrectiveActionReviewInput>;

// ----------------------------------------------------------------------
// Library documents (read-only reference docs for companies / drivers)
// ----------------------------------------------------------------------

export const LibraryAudience = z.enum(['company', 'driver', 'both']);
export type LibraryAudience = z.infer<typeof LibraryAudience>;

export const LibraryDocumentUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
});
export type LibraryDocumentUploadUrlInput = z.infer<typeof LibraryDocumentUploadUrlInput>;

export const LibraryDocumentCreateInput = z.object({
  title: z.string().min(1).max(255),
  description: z.string().max(2000).nullable().optional(),
  category: z.string().max(120).nullable().optional(),
  audience: LibraryAudience.default('both'),
  file_path: z.string().min(1),
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
  size_bytes: z.number().int().min(0),
});
export type LibraryDocumentCreateInput = z.infer<typeof LibraryDocumentCreateInput>;

export const LibraryDocumentUpdateInput = z
  .object({
    title: z.string().min(1).max(255).optional(),
    description: z.string().max(2000).nullable().optional(),
    category: z.string().max(120).nullable().optional(),
    audience: LibraryAudience.optional(),
    is_active: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type LibraryDocumentUpdateInput = z.infer<typeof LibraryDocumentUpdateInput>;

// ----------------------------------------------------------------------
// Visitors
// ----------------------------------------------------------------------

export const VisitorAnswerInput = z.object({
  question_id: z.string().uuid(),
  answer: z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]).optional(),
});
export type VisitorAnswerInput = z.infer<typeof VisitorAnswerInput>;

export const VisitorRegisterInput = z.object({
  name: z.string().min(1).max(255),
  email: z.string().email().max(255).nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  company_name: z.string().max(255).nullable().optional(),
  // Who the visitor is here to see — required so every visit records a host.
  host_name: z.string().min(1).max(255),
  // Optional link to the chosen staff member (when picked from the host list);
  // when set, that host is emailed on arrival.
  host_user_id: z.string().uuid().nullable().optional(),
  purpose: z.string().max(500).nullable().optional(),
  site: z.string().max(255).nullable().optional(),
  proof_method: z.enum(['label', 'sms']).default('label'),
  answers: z.array(VisitorAnswerInput).max(100).optional(),
});
export type VisitorRegisterInput = z.infer<typeof VisitorRegisterInput>;

// ----------------------------------------------------------------------
// Locate2u integration (admin Settings → Integrations)
// ----------------------------------------------------------------------

export const Locate2uConnectInput = z.object({
  client_id: z.string().min(1).max(255),
  client_secret: z.string().min(1).max(255),
});
export type Locate2uConnectInput = z.infer<typeof Locate2uConnectInput>;

export const Locate2uSyncDriversInput = z.object({
  // Fallback company for team members whose `company` field doesn't match any
  // companies.company_name (or is absent — the field isn't live yet).
  override_company_id: z.string().uuid().optional(),
});
export type Locate2uSyncDriversInput = z.infer<typeof Locate2uSyncDriversInput>;

// ----------------------------------------------------------------------
// Branding
// ----------------------------------------------------------------------
export const CompanyInfoLabels = z
  .object({
    contractorType: z.string().min(1).max(100).optional(),
    companyNumber: z.string().min(1).max(100).optional(),
    contractNumber: z.string().min(1).max(100).optional(),
    teamRegion: z.string().min(1).max(100).optional(),
    primaryContact: z.string().min(1).max(100).optional(),
  })
  .optional();
export type CompanyInfoLabels = z.infer<typeof CompanyInfoLabels>;

export const Branding = z.object({
  companyName: z.string(),
  tagline: z.string().optional(),
  logoUrl: z.string().url().optional(),
  primaryColor: z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/),
  primaryDark: z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/),
  accentColor: z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/),
  welcomeHeading: z.string(),
  welcomeMessage: z.string(),
  emailFromName: z.string(),
  emailFromAddress: z.string().email(),
  companyInfoLabels: CompanyInfoLabels,
  contractorTypeOptions: z.array(z.string().min(1).max(120)).optional(),
});
export type Branding = z.infer<typeof Branding>;

export const COMPANY_INFO_LABEL_DEFAULTS = {
  contractorType: 'Contractor Type',
  companyNumber: 'Company Number',
  contractNumber: 'Contract Number',
  teamRegion: 'Team Region',
  primaryContact: 'Primary Contact',
} as const;
